import { defModule, type Module, UpdateResult } from '@st/redux'
import { DocumentViewerCommandMessage } from './document-viewer-command'
import {
  DocumentViewerViewportMessage,
  DocumentViewerViewportState,
  fitToWidthZoom,
  reduceDocumentViewerViewportState
} from './document-viewer-viewport-module'
import { selPageTransform } from './viewer-geometry'

export const DOCUMENT_VIEWER_MODULE_NAME = 'documentViewer'

export type DocumentViewerState = DocumentViewerViewportState

export type DocumentViewerCoreMessage = DocumentViewerViewportMessage | DocumentViewerCommandMessage

export function makeDocumentViewerModule(
  viewportSettingsKey?: string
): Module<DocumentViewerState, DocumentViewerCoreMessage> {
  const initialState: DocumentViewerState = {
    viewerShell: {
      zoom: 1,
      cssPxPerPdfPt: 1
    },
    viewportMeasurement: null,
    followsFitToWidth: true
  }

  return defModule<{ state: DocumentViewerState; message: DocumentViewerCoreMessage }>({
    init: () => initialState,
    update: (state, message) => reduceDocumentViewerMessage(state, message, viewportSettingsKey)
  })
}

function reduceDocumentViewerMessage(
  state: DocumentViewerState,
  message: DocumentViewerCoreMessage,
  viewportSettingsKey?: string
): UpdateResult<DocumentViewerState, DocumentViewerCoreMessage> {
  if (message.type == 'viewerCommand') {
    return state
  }

  const viewportResult = reduceDocumentViewerViewportState(state, message, viewportSettingsKey)
  if (!viewportResult) return state
  return viewportResult
}

export function selViewerZoomLevel(state: DocumentViewerState) {
  return state.viewerShell.zoom
}

/**
 * The zoom that would fit a page of the given width to the measured container;
 * undefined until the container has been measured.
 */
export function selViewerFitToWidthZoom(
  state: DocumentViewerState,
  pageWidthPt: number | undefined
): number | undefined {
  const measurement = state.viewportMeasurement
  if (!measurement || pageWidthPt == undefined) return undefined
  return fitToWidthZoom(measurement.containerWidthPx, pageWidthPt, measurement.cssPxPerPdfPt)
}

export function selViewerCssPxPerPdfPt(state: DocumentViewerState) {
  return state.viewerShell.cssPxPerPdfPt
}

export function selViewerPageTransform(state: DocumentViewerState) {
  return selPageTransform(state.viewerShell.zoom, state.viewerShell.cssPxPerPdfPt)
}
